/**
 * GlobalHumChat - Global HumChat AI Assistant available across all wireframe pages
 *
 * Features:
 * - Collapsible sidebar panel on the right
 * - Available on all wireframe pages via context
 * - Open by default on Knowledge Hub, closed on other pages
 * - Maintains chat history across page navigation
 */

import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, Globe, ChevronRight, ChevronLeft, RefreshCw, User, Copy, ThumbsUp, ThumbsDown, Send } from 'lucide-react';
import { cn } from '../../lib/utils';

// ========== Types ==========
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  feedback?: 'positive' | 'negative' | null;
}

interface HumChatContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleChat: () => void;
  messages: Message[];
  sendMessage: (content: string) => void;
  clearChat: () => void;
}

// ========== Context ==========
const HumChatContext = createContext<HumChatContextType | undefined>(undefined);

export function useHumChat() {
  const context = useContext(HumChatContext);
  if (!context) {
    throw new Error('useHumChat must be used within a HumChatProvider');
  }
  return context;
}

// ========== Welcome Message ==========
const welcomeMessage: Message = {
  id: 'welcome',
  role: 'assistant',
  content: `Hello! I'm **HumChat**, your AI assistant for humanitarian operations. I'm trained on IASC guidelines, Sphere standards, and context-specific humanitarian data.

I can help you with:
- **Situation analysis** and crisis briefs
- **Response planning** and resource allocation
- **Protocol guidance** for emergencies
- **Coordination** support and communications

What humanitarian challenge can I help you address today?`,
  timestamp: new Date()
};

// ========== Example Questions ==========
const exampleQuestions = [
  "Analyze displacement trends in Somali Region",
  "What are the priority interventions for health emergencies?",
  "Generate a rapid assessment plan for protection risks",
  "What coordination mechanisms should we establish?",
];

// ========== AI Response Generator ==========
const generateResponse = (userMessage: string): string => {
  const lowerMessage = userMessage.toLowerCase();

  if (lowerMessage.includes('displacement') || lowerMessage.includes('trend') || lowerMessage.includes('analyze')) {
    return `## Displacement Analysis: Somali Region

### Current Situation
- **Total IDPs**: 1.2M (as of Jan 2026)
- **New displacements (30 days)**: 45,200
- **Primary drivers**: Drought (62%), Conflict (28%), Floods (10%)

### Key Trends
1. **Westward movement** toward urban centers increasing
2. **Secondary displacement** from overcrowded sites
3. **Cross-border movement** to Kenya decreasing

### Recommendations
- Expand site capacity in Jigjiga zone
- Pre-position NFI stock in Gode
- Strengthen protection monitoring`;
  }

  if (lowerMessage.includes('health') || lowerMessage.includes('priority') || lowerMessage.includes('intervention')) {
    return `## Priority Health Interventions

### Immediate Priorities (0-72 hours)
1. **Cholera response** - ORS distribution & treatment centers
2. **Maternal health** - Emergency obstetric care
3. **Malnutrition** - SAM/MAM screening & treatment

### Resource Requirements
- 50,000 ORS sachets
- 200 cholera treatment kits
- 15 additional health workers
- Mobile health teams (3 units)

### Coordination Points
- WHO: Lead agency for health cluster
- UNICEF: Nutrition response
- MSF: Emergency surgical capacity`;
  }

  if (lowerMessage.includes('assessment') || lowerMessage.includes('protection') || lowerMessage.includes('rapid')) {
    return `## Rapid Protection Assessment Plan

### Assessment Objectives
1. Identify protection risks and vulnerable groups
2. Map existing protection services
3. Assess community-based protection mechanisms

### Methodology
- **Duration**: 5 days
- **Team**: 4 protection officers, 2 interpreters
- **Data collection**: KIIs, FGDs, observation

### Key Indicators
- GBV incident reporting rates
- Child protection concerns
- Access to documentation
- Freedom of movement`;
  }

  if (lowerMessage.includes('coordination') || lowerMessage.includes('mechanism')) {
    return `## Coordination Mechanisms

### Recommended Structure
**Strategic Level**: Inter-cluster (Weekly), Govt liaison (Bi-weekly)
**Operational Level**: Sector working groups (Weekly)
**Field Level**: Site coordination (Daily)

### Key Stakeholders
| Organization | Role |
|--------------|------|
| OCHA | Coordination lead |
| NRC | Camp management |
| WHO | Health cluster |
| WFP | Food security |`;
  }

  if (lowerMessage.includes('brief') || lowerMessage.includes('situation') || lowerMessage.includes('overview')) {
    return `## Situation Brief - Ethiopia Operations

### Active Crises
1. **Cholera Outbreak** - Somali Region (Severity: High)
2. **Flood Emergency** - Afar Region (Severity: Moderate)
3. **Drought Response** - Oromia Region (Severity: Moderate)

### Key Metrics
- Total affected population: 2.4M
- Response coverage: 67%
- Funding gap: 33%

### Priority Actions
1. Scale up WASH response in Somali region
2. Pre-position NFI kits in flood-prone areas
3. Activate cash transfer programs in Oromia`;
  }

  return `Thank you for your question. I can help you with:

1. **Analysis** - Situation briefs, trend analysis, risk assessments
2. **Planning** - Response planning, resource allocation
3. **Protocols** - SOPs, guidelines, standards
4. **Coordination** - Meeting briefs, stakeholder communications

Please provide more details about the specific crisis, location, or type of support you need.`;
};

// ========== HumChat Provider ==========
interface HumChatProviderProps {
  children: ReactNode;
  defaultOpen?: boolean;
}

export function HumChatProvider({ children, defaultOpen }: HumChatProviderProps) {
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([welcomeMessage]);

  // Determine if chat should be open based on current page
  const isKnowledgeHub = location.pathname === '/wireframes/knowledge';
  const [isOpen, setIsOpen] = useState(defaultOpen ?? isKnowledgeHub);

  // Update open state when navigating to/from Knowledge Hub
  useEffect(() => {
    if (isKnowledgeHub) {
      setIsOpen(true);
    }
  }, [isKnowledgeHub]);

  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    // Simulate AI response delay
    setTimeout(() => {
      const response = generateResponse(content);
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    }, 1000 + Math.random() * 1500);
  }, []);

  const clearChat = useCallback(() => {
    setMessages([welcomeMessage]);
  }, []);

  return (
    <HumChatContext.Provider value={{ isOpen, setIsOpen, toggleChat, messages, sendMessage, clearChat }}>
      {children}
    </HumChatContext.Provider>
  );
}

// ========== HumChat Sidebar Component ==========
export function HumChatSidebar() {
  const { isOpen, toggleChat, messages, sendMessage, clearChat } = useHumChat();
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(messages.length);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > prevMessagesLength.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessagesLength.current = messages.length;
  }, [messages]);

  // Track typing state
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'user') {
      setIsTyping(true);
    } else {
      setIsTyping(false);
    }
  }, [messages]);

  const handleSendMessage = () => {
    if (!inputValue.trim() || isTyping) return;
    sendMessage(inputValue);
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFeedback = (messageId: string, feedback: 'positive' | 'negative') => {
    // In a real app, this would send feedback to the server
    console.log('Feedback:', messageId, feedback);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const renderMarkdown = (content: string) => {
    let html = content
      .replace(/^### (.*$)/gim, '<h3 class="text-sm font-semibold text-gray-900 mt-3 mb-1">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-base font-semibold text-gray-900 mt-3 mb-2">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc text-[13px]">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal text-[13px]">$1</li>')
      .replace(/\|/g, ' | ')
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br />');
    return <div dangerouslySetInnerHTML={{ __html: `<p class="mb-2">${html}</p>` }} />;
  };

  return (
    <>
      {/* Toggle button - always visible when closed */}
      <div
        className={cn(
          'fixed right-0 top-1/2 -translate-y-1/2 z-50 transition-all duration-300 ease-in-out',
          isOpen ? 'opacity-0 pointer-events-none translate-x-full' : 'opacity-100 translate-x-0'
        )}
      >
        <button
          onClick={toggleChat}
          className="flex items-center gap-2 bg-accent-500 text-white px-3 py-4 shadow-lg hover:bg-accent-600 transition-colors rounded-l-lg"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          <MessageCircle className="w-4 h-4 rotate-90" />
          <span className="text-sm font-medium">HumChat AI</span>
          <ChevronLeft className="w-4 h-4 rotate-90" />
        </button>
      </div>

      {/* Sidebar panel with slide animation */}
      <div
        className={cn(
          'fixed right-0 top-0 h-full w-[380px] z-40 flex flex-col bg-white border-l border-gray-200 shadow-xl transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-accent-500 flex items-center justify-center rounded">
            <Globe className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">HumChat AI</h3>
            <p className="text-[10px] text-gray-500">Humanitarian AI Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 border border-green-200 rounded">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            Online
          </span>
          <button
            onClick={clearChat}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Clear chat"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={toggleChat}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Close panel"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((message) => (
          <div key={message.id} className={cn('flex gap-2', message.role === 'user' ? 'flex-row-reverse' : '')}>
            <div className={cn('w-6 h-6 flex-shrink-0 flex items-center justify-center rounded', message.role === 'assistant' ? 'bg-accent-100' : 'bg-gray-200')}>
              {message.role === 'assistant' ? <Globe className="w-3 h-3 text-accent-600" /> : <User className="w-3 h-3 text-gray-600" />}
            </div>
            <div className={cn('flex-1', message.role === 'user' ? 'text-right' : '')}>
              {message.role === 'assistant' && <div className="text-[10px] text-accent-600 font-medium mb-1">HAI</div>}
              <div className={cn('inline-block text-left px-3 py-2 text-[13px] rounded', message.role === 'assistant' ? 'bg-gray-50 border border-gray-200' : 'bg-accent-500 text-white')}>
                {message.role === 'assistant' ? (
                  <div className="prose prose-sm max-w-none text-gray-700">{renderMarkdown(message.content)}</div>
                ) : (
                  <p className="whitespace-pre-wrap">{message.content}</p>
                )}
              </div>
              {message.role === 'assistant' && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[9px] text-gray-400">
                    {message.timestamp.toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {message.id !== 'welcome' && (
                    <>
                      <button onClick={() => copyToClipboard(message.content)} className="p-1 text-gray-400 hover:text-gray-600 rounded" title="Copy">
                        <Copy className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleFeedback(message.id, 'positive')} className="p-1 text-gray-400 hover:text-green-600 rounded" title="Helpful">
                        <ThumbsUp className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleFeedback(message.id, 'negative')} className="p-1 text-gray-400 hover:text-red-600 rounded" title="Not helpful">
                        <ThumbsDown className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex gap-2">
            <div className="w-6 h-6 bg-accent-100 flex items-center justify-center rounded">
              <Globe className="w-3 h-3 text-accent-600" />
            </div>
            <div className="bg-gray-50 border border-gray-200 px-3 py-2 rounded">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3 bg-gray-50">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about humanitarian response..."
            className="flex-1 px-3 py-2 border border-gray-200 bg-white text-[13px] rounded outline-none focus:border-accent-500"
            disabled={isTyping}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isTyping}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded transition-colors',
              inputValue.trim() && !isTyping ? 'bg-accent-500 text-white hover:bg-accent-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            Send
          </button>
        </div>

        {/* Example Questions */}
        <div className="mt-2 space-y-1">
          {exampleQuestions.slice(0, 3).map((q, i) => (
            <button
              key={i}
              onClick={() => { setInputValue(q); }}
              className="block w-full text-left text-[11px] text-gray-500 hover:text-accent-500 truncate"
            >
              "{q}"
            </button>
          ))}
        </div>
      </div>
      </div>
    </>
  );
}

// ========== Exports ==========
export default HumChatSidebar;
